import { GOODS_ENDPOINT, webCore } from '@lemon/web-core';

import type { UpdatePouchDTO } from '../types';
import type { PouchBody, PouchView, ProductView } from '@lemoncloud/epyt-goods-api';
import type { PaginatedListResult } from '@lemoncloud/epyt-goods-api/dist/cores/types';
import type { Params } from '@lemoncloud/lemon-web-core';

const GOODS_API = GOODS_ENDPOINT;

export const analyzePouchByIdPublic = async (params: Params): Promise<ProductView> => {
    const { id, ...restParams } = params;
    if (!id) {
        throw new Error('analyzePouchByIdPublic: @id is required');
    }

    const { data } = await webCore
        .buildRequest({
            method: 'GET',
            baseURL: `${GOODS_API}/public/${id}/analyze`,
        })
        .setParams({ force: true, ...restParams })
        .execute<ProductView>();

    return data;
};

export const analyzePouchById = async (params: Params): Promise<ProductView> => {
    const { id, ...restParams } = params;
    if (!id) {
        throw new Error('analyzePouchById: @id is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${GOODS_API}/pouches/${id}/analyze`,
        })
        .setParams({ force: true, ...restParams })
        .execute<ProductView>();

    return data;
};

export const fetchPouches = async (params: Params): Promise<PaginatedListResult<PouchView>> => {
    const { data } = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${GOODS_API}/pouches/0/list`,
        })
        .setParams(params)
        .execute<PaginatedListResult<PouchView>>();

    return { ...data };
};

export const fetchPouchById = async (id?: string): Promise<PouchView> => {
    if (!id) {
        throw new Error('fetchPouchById: @id is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${GOODS_API}/pouches/${id}`,
        })
        .execute<PouchView>();

    return data;
};

export const createPouch = async (body: PouchBody): Promise<PouchView> => {
    const res = await webCore
        .buildSignedRequest({
            method: 'POST',
            baseURL: `${GOODS_API}/pouches/0`,
        })
        .setBody({ ...body })
        .execute<PouchView>();

    return res.data;
};

export const updatePouch = async (body: UpdatePouchDTO): Promise<PouchView> => {
    const { pouchId, ...restData } = body;
    if (!pouchId) {
        throw new Error('updatePouch: @pouchId is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'PUT',
            baseURL: `${GOODS_API}/pouches/${pouchId}`,
        })
        .setBody(restData)
        .execute<PouchView>();

    return data;
};

export const deletePouch = async (id: string): Promise<PouchView> => {
    if (!id) {
        throw new Error('deletePouch: @id is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'DELETE',
            baseURL: `${GOODS_API}/pouches/${id}`,
        })
        .setBody({})
        .execute<PouchView>();

    return data;
};
